//@ts-check
import React from 'react';
import { useApolloClient, ApolloClient, ApolloError, QueryOptions, gql } from '@apollo/client';

import { generateShopifyGraphQLId, getIdFromShopifyGraphQLId } from '../../../utils/shopify';
import { WindowServerPdpData } from '../types/ServerPDPData';
import { ProductData, ProductsEdge, VariantNode, Images, Media } from '../types/GraphqlPDPDetailsData';
import PDP from './PDP';
import {
  isArray,
  uniq,
  intersection,
  isEmpty,
  flatten,
  sortBy,
  groupBy,
  first,
  keyBy,
  uniqBy,
  mapValues,
} from 'lodash-es';
import { isNil } from 'lodash-es';
import { AvailableColorData, IVariant, IColorwayProductData, IProduct, PdpVariations, IPdpData } from '../types/PDP';
import { usePromise } from '../../_shared/react-hooks';
import { heapTrack } from '../../../utils/helpers';
import { NonEmptyArray, notEmpty, notNil } from '../../../utils/typescriptUtils';
import { isProduction, InitialUrlSearchParams } from '../../../utils/environment';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { isForcedMatchingSet } from '../variations/forcedMatchingSets'; // temporary
import { PRODUCT_TILE_FALLBACK_IMG } from '../../../utils/constants';
import { SiteSettings } from '../../../utils/environment';

const PDPContainer = () => {
  //@ts-ignore
  const serverPdpData: WindowServerPdpData = window.server_pdp_data;
  const apolloClient = useApolloClient();

  // Pulling PDP Product Shell data from server passed data
  const {
    product: { images: productImages, media: productMedia },
  } = serverPdpData;

  // ? [REVISIT] add real ts support
  const { isLoading: loading, error, data } = usePromise<ProductData[][], ApolloError>(
    pdpDataFetcher(apolloClient, serverPdpData)
  );

  if (loading) return <p className="centered-message nativeapp-hide">Loading&hellip;</p>;
  if (error || !data) {
    if (error) {
      console.log('PDPContainer data fetching error: ', error);
      if (error?.networkError) window.location.reload();
      throw error;
    }
    // what does this mean? likely just a TS issue
    throw new Error('PDPContainer failed to load data & has no error.');
  }
  const productDataGroups: ProductData[][] = data;

  const processedPdpProductShellData = processPdpProductShellData(serverPdpData);
  const products = processProductVariantsData(productDataGroups);
  const validProducts = products.filter((item) => item.variants.length);

  const isASet = validProducts.length > 1;

  const images = isASet
    ? productImages
    : uniqBy(
        flatten((first(productDataGroups) || []).map((productData) => extractImageUrls(productData.images))),
        (item) => item.replace(/\?v=.*/g, '').replace(GUID_REGEX, '')
      );

  const PDP_DATA = {
    // ? [REVISIT]
    assets: {
      // ? [REVISIT] this is not part of the types
      productVideos: productMedia?.filter((item) => item.media_type === 'video') ?? [],
      // ? [REVISIT] after we solve SETs 'issue'
      images: (notEmpty(images) ? images : [PRODUCT_TILE_FALLBACK_IMG]) as NonEmptyArray<string>,
    },
    variations: getPdpVariations(serverPdpData),
    products: validProducts,
    ...processedPdpProductShellData,
    isASet,
  };

  if (products.length !== validProducts.length) {
    const { handle, title } = processedPdpProductShellData.pdpProductShell;
    const fallbackProductHandle = first(Object.values(first(validProducts)?.productDataByColorName ?? {}))?.handle;
    if (products.length > 1 && validProducts.length === 1 && fallbackProductHandle) {
      heapTrack('pdp bad set', { handle, title });
      if (isProduction) window.location.replace(`/products/${fallbackProductHandle}?badSet=${handle}`);
      else throw new Error("Only one of the set's 'products' is valid");
    } else if (validProducts?.length === 0) {
      //technically mutually exclusive events
      heapTrack('pdp bad set', { handle, title, availableProducts: 0 });
      if (isProduction) window.location.replace(`/search?q=${title.replace(' set', '')}`);
      else throw new Error('PDP computed 0 valid products');
    }
  }

  if (notEmpty(PDP_DATA.products)) {
    const nonEmptyProducts = PDP_DATA.products;
    const typeSafePdpData: IPdpData = { ...PDP_DATA, products: nonEmptyProducts };

    return <PDP pdpData={typeSafePdpData} />;
  } else throw new Error('No products');
};

export default PDPContainer;

type AdvancedSetConfig = {
  handles: string[];
}[];
// ? [REVISIT] should it be `ProductData[][]` or `(ProductData | null)[][]`; when passed bad values does the API error out OR does it return nulls for missing entries?
// -- test by passing bad data `pdpDataFetcher`
// ? [REVISIT] need TS bad so we can auto-generate the GraphQL types
const pdpDataFetcher = (apolloClient: ApolloClient<object>, serverPdpData: WindowServerPdpData) => async () => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const apolloClientQueryWithError = (options: QueryOptions<Record<string, any>>) =>
    apolloClient.query({ ...options, errorPolicy: 'all' }).then((result) => {
      if (result.error) throw result.error;
      return result;
    });
  //@ts-expect-error
  const advancedSetConfigMetafieldData: AdvancedSetConfig | string = serverPdpData.metaFields.sets.advanced_set_config;
  if (advancedSetConfigMetafieldData) {
    // depending on what shape/type we get back we would need to JSON.parse or not
    let advancedSetConfig: AdvancedSetConfig;
    try {
      if (typeof advancedSetConfigMetafieldData === 'string') {
        advancedSetConfig = JSON.parse(advancedSetConfigMetafieldData);
      } else {
        advancedSetConfig = advancedSetConfigMetafieldData;
      }
    } catch (err) {
      // ? [REVISIT] what do? throw?
      throw new Error(`'advanced_set_config' metafield misconfigured`);
    }

    const productHandlesToFetch = uniq(advancedSetConfig.flatMap((item) => item.handles)).map((item) => item.trim());

    const graphqlQuery = generateGetPdpDataQueryByHandles(productHandlesToFetch);
    const { data } = await apolloClientQueryWithError({
      query: graphqlQuery,
    });
    //@ts-expect-error
    const productsData: ProductData[] = Object.values(data).filter((item) => item);

    const productsDataByHandle = keyBy(productsData, (item) => item.handle);
    const productDataGroups = advancedSetConfig.map(({ handles }) => {
      const productsForGroup = handles.map((handle) => {
        // TS 4.1 :)
        const matchingProductData: ProductData | undefined = productsDataByHandle[handle];
        // ! [REVISIT] what do; crash or remove productgroup? or remove just the 1 product?
        if (!matchingProductData) throw new Error('Failed to fetch products');
        return matchingProductData;
      });
      // .filter(notNil);
      return productsForGroup;
    });
    // generateHashForLineItemScripts(productDataGroups);
    return productDataGroups;
  }

  const isASet = checkIfSetProduct(serverPdpData);
  //if !isASet
  if (isASet) {
    if (!serverPdpData.metaFields.sets.colorway_product_handles) throw new Error('Should be impossible to get here');
    const productHandlesToFetch = serverPdpData.metaFields.sets.colorway_product_handles
      .split(',')
      .map((item) => item.trim());

    const graphqlQuery = generateGetPdpDataQueryByHandles(productHandlesToFetch);
    const { data } = await apolloClientQueryWithError({
      query: graphqlQuery,
    });

    // ? [REVISIT] why does the following set of hash-->array-->hash-->[][] produce a consistent/desired ordering?
    //@ts-ignore
    const productsData: ProductData[] = Object.values(data).filter((item) => item);
    const groupedByStyleId = groupBy(productsData, (productData) => computeStyleId(productData.handle));
    const productDataGroups = Object.values(groupedByStyleId);
    return productDataGroups;
  } else {
    const {
      product: { tags, id },
    } = serverPdpData;
    const styleId = tags.find((tag) => tag.toLowerCase().startsWith('styleid:'));
    // ? [REVISIT] should we default this? & to what?
    const pricingTag = tags.find((tag) => tag.startsWith('pricing:'));
    const pricingQuery = !groupFullPriceSalePriceSwatches && pricingTag ? `tag:'${pricingTag}'` : '';

    // ? [REVISIT] additional restriction like sale products, 'Policy:EarlyAccess', 'DoNotPublish', 'available_for_sale'  etc. maybe after we rewrite PLP
    // ? [REVISIT] `available_for_sale:true`
    const productSearchQuery = `tag:'${styleId}' ${pricingQuery} `;
    // w/ real TS add annotations
    const productsDataPromise = styleId
      ? apolloClientQueryWithError({
          query: GetPdpDataByColorwayProductsQuery,
          variables: { productSearchQuery },
        }).then((resp) => {
          const edges: ProductsEdge[] = resp.data.products.edges;
          return edges.map((edge) => edge.node);
        })
      : apolloClientQueryWithError({
          query: GetPdpDataByProductIdsQuery,
          variables: { ids: [generateShopifyGraphQLId('Product', id.toString())] },
        }).then((resp) => {
          const nodes: ProductData[] = resp.data.nodes;
          return nodes;
        });

    const productsData = await productsDataPromise;
    return [productsData.filter((item) => item)];
  }
};

const getPdpVariations = (landedProductData: WindowServerPdpData): PdpVariations => {
  return {
    ForcedMatchingSet: isForcedMatchingSet(landedProductData.product.tags),
  };
};

// Data Sanitization
const processPdpProductShellData = (productShellData: WindowServerPdpData) => {
  // ? [REVISIT] how many of these need to pulled from somewhere else?
  const {
    customerEmail,
    metaFields: { attribs, stylistChat, aloReviews },
    product,
  } = productShellData;
  const { description, tags, title, handle, compare_at_price, price } = product;
  return {
    customer: {
      email: customerEmail,
    },
    pdpProductShell: {
      title,
      handle,
      descriptionHtml: description,
      tags,
      attributes: attribs,
      aloReviews,
      price: price / 100,
      compareAtPrice: compare_at_price ? compare_at_price / 100 : compare_at_price,
      stylistChat: {
        isEnabled: stylistChat.is_enabled === 'true' && !!stylistChat.phone_number,
        pdpHeader: stylistChat.pdp_header_copy,
        pdpMessage: stylistChat.pdp_message_copy,
        phoneNumber: stylistChat.phone_number,
        phoneMessage: stylistChat.phone_message,
      },
    },
  };
};

const isPreviewMode =
  window.location.host.includes('shopifypreview.com') || window.location.search.includes('preview_key');
const viewUnpublished = isPreviewMode || !isNil(InitialUrlSearchParams.get('viewUnpublished'));

const processProductVariantsData = (productsData: ProductData[][]) => {
  //@ts-ignore
  const serverPdpData: WindowServerPdpData = window.server_pdp_data;
  const isASet = checkIfSetProduct(serverPdpData);
  const newProductsData = productsData.map((productsGroup) => {
    const aggregateVariants = flatten(
      productsGroup
        // -- Proactively removing un-published products from the "list": allowing that swatch/color to be selected would result in a 404 on refresh (products can be published to the PDP channel but not the online store)
        .filter((item) => item && (item.onlineStoreUrl || (!isProduction && viewUnpublished) || isPreviewMode)) // incase the product comes back as null
        .map(
          (item) =>
            item.variants.edges
              .map((edge) => edge.node)
              // * [RANT] exploration of real time data fetching/crunching ... vs ... AOT bots computations
              // ? [REVISIT] this effectively shows the PDP can be faster/"better" than the bots :); bots are pretty much only needed for the PLP
              .filter((variantNode) => {
                if (!isASet) return true;
                // ? [REVISIT][TEMPORARY] until the colorway_product_handles are propulated w/ only full priced products
                // if 'filters' could be derivered from context (metafields, query params, location, we wouldn't need extra simples)
                const showSalePricing = true;
                const hasSalePrice = !!variantNode.compareAtPriceV2;
                // ? [REVISIT] What the f*&K is this here for? If a set, kill all sale items in the set???
                return showSalePricing || !hasSalePrice || variantNode.sku === 'ALO01001YR';
              })
          // ? [REVISIT] example of "colorbot" w/o using available colors (for non-sets)
          // .filter(variantNode => {
          //   if (isASet) return true;
          //   // ? [REVISIT] TBD how to do this pattern for sets ... use query params? ... keep thinking
          //   const landedProductData = serverPdpData.product;
          //   // -- could be extended to cool stuff based on inventory / tags; or variant price ranges, etc.
          //   const { available, compare_at_price } = landedProductData;
          //   return (
          //     (available ? variantNode.availableForSale : true) &&
          //     !!compare_at_price === !!variantNode.compareAtPriceV2
          //   );
          // })
          // ? ^^^ can be extended to solve the "simples" problem space (curve, etc.) + if the PLP was CSR BOOM no bots + easier merch
          // ? -- entended by either: 1) fetching all the products data for collections (given they are tiny) & client-side filter + search ... sounds bad BUT there are no repeat requests for collections & small requests for subsequent PDP fetches; 2) use the `products(query:)` to make precise network calls
        )
    );
    const parsedVariantsData = aggregateVariants.map(parseGraphqlVariantNode);

    // -- w/ optional chaning --> 1 liner
    const productsWithColorname = productsGroup.map((productData) => {
      const variant = productData.variants.edges.find((item) => item.node.selectedOptions);
      // ? [REVISIT] introducing the garbarge we do everywhere else (assumptions about color); bad thing for sure but for 'consistently' until we update how we treat selections RIP 💀 ... if you can beat them, give up
      const option = variant && variant.node.selectedOptions[0];
      const colorName = (option && option.value) || PDP_COLOR_DEFAULT;
      return { ...productData, colorName };
    });
    const productDataByColorName = keyBy(
      productsWithColorname.map(
        ({
          variants,
          images,
          __typename,
          id,
          media,
          tags,
          totalInventory,
          ...restOfProductData
        }): IColorwayProductData => ({
          ...restOfProductData,
          // ? [REVISIT] make sure this `!` is safe;
          id: getIdFromShopifyGraphQLId(id)!,
          variants: variants.edges.map((edge) => parseGraphqlVariantNode(edge.node)),
          images: extractImageUrls(images),
          videos: extractVideoUrls(media),
          baseTitle: restOfProductData.title.replace(` - ${restOfProductData.colorName}`, ''),
          tags,
          totalInventory,
        })
      ),
      'colorName'
    );

    let availableColors: AvailableColorData[] = []; // ? [REVISIT] what does this mean for sets?
    if (
      !isASet &&
      //@ts-ignore
      serverPdpData.metaFields.aloSwatch &&
      //@ts-ignore
      serverPdpData.metaFields.aloSwatch['available-colors']
    ) {
      try {
        // ? [REVISIT]
        //@ts-ignore
        const json = serverPdpData.metaFields.aloSwatch['available-colors'];
        if (isArray(json)) {
          // ? [REVISIT] fix this data issue, how did this happen?
          availableColors = json.filter((item) => item.name !== 'undefined');
        }
      } catch (err) {
        console.error("Failed to parse 'availableColors' metafield, for product: ", serverPdpData);
      }
    }
    // ? [REVISIT] still need to find out, what does this mean for sets?
    let productAvailableColors: AvailableColorData[] = [];
    if (isASet) {
      try {
        // ? [REVISIT]
        //@ts-ignore
        const json = JSON.parse(productsGroup[0].availableColors.value);

        if (isArray(json)) {
          // ? [REVISIT] fix this data issue, how did this happen?
          productAvailableColors = json.filter((item) => item.name !== 'undefined');
        }
      } catch (err) {
        console.error("Failed to parse 'availableColors' metafield, for product: ", productsGroup);
      }
    }
    const uniqueVariantColorsList = uniq(parsedVariantsData.map((item) => item.selectionOptions.Color));
    const availableColorsList =
      !viewUnpublished && availableColors.filter(Boolean).length > 0
        ? availableColors.map((color) => color.name).filter(Boolean)
        : [];

    const filteredColorList =
      isEmpty(availableColorsList) || groupFullPriceSalePriceSwatches
        ? uniqueVariantColorsList
        : intersection(uniqueVariantColorsList, availableColorsList);

    const sortedColorsList = sortBy(filteredColorList, (colorName) => {
      const availableColor = (availableColors || []).find((item) => item.name === colorName);

      // ? [REVISIT] should we sort for sets?
      return isASet ? 0 : availableColor ? availableColor.sortScore : -100;
    }).reverse();

    const uniqueSizes = uniq(parsedVariantsData.map((item) => item.selectionOptions.Size));

    const swatchUiDataByColor = mapValues(keyBy(availableColors, 'name'), 'swatch');

    const parsedProductData: IProduct = {
      variants: parsedVariantsData,
      availableColors: availableColors,
      // ? [REVISIT] this doesn't seem to be used & we are passing 2 fields that could be consolidated?
      productAvailableColors: productAvailableColors,
      swatchUiDataByColor,
      options: {
        Color: sortedColorsList,
        Size: sortBy(uniqueSizes, (size) => SIZES.findIndex((_size) => _size === size.toLowerCase())),
      },
      productDataByColorName,
      productType: productsGroup[0] ? productsGroup[0].productType : '',
    };
    return parsedProductData;
  });

  return newProductsData;
};

export interface SwatchSection {
  color?: string;
  image?: string;
}

const SIZES = ['xxs', 'xs', 's', 'm', 'l', 'xl', '2xl'];
// ? [REVISIT] PDP shouldn't be defaulting colors, but necessary until we remove color assumptions
const PDP_COLOR_DEFAULT = 'nocolor';

const parseGraphqlVariantNode = (variantData: VariantNode): IVariant => {
  const {
    availableForSale,
    id,
    image,
    selectedOptions,
    sku,
    priceV2,
    compareAtPriceV2,
    quantityAvailable,
    requiresShipping,
    estimatedShipStartDate,
  } = variantData;

  // ? [REVISIT] we can't use the solution below as our internal data structure is wrong ...
  // ? `userSelections:{Color, Size}` vs `userSelections:[option1,2,3]`...assuming 'Color' / 'Size' breaks less atm 💀
  // const colorSelection = selectedOptions.find(item => item.name === 'Color') || { value: 'nocolor' };
  // const sizeSelection = selectedOptions.find(item => item.name === 'Size') || { value: 'onesize' };
  // !!! [REVISIT]? don't just assume 'Color' / 'Size'
  const [colorSelection = { value: 'nocolor' }, sizeSelection = { value: 'onesize' }] = selectedOptions;

  return {
    availableForSale,
    quantityAvailable,
    // ? [REVISIT] make sure this `!` is safe;
    id: getIdFromShopifyGraphQLId(id)!,
    imageUrl: image ? image.url : '', //  ? [REVISIT]
    selectionOptions: {
      Color: colorSelection.value,
      Size: sizeSelection.value,
    },
    sku,
    requiresShipping,
    estimatedShipStartDate,
    price: Number(priceV2.amount),
    displayPrice: compareAtPriceV2 && Number(compareAtPriceV2.amount),
  };
};

const extractImageUrls = (images: Images) => images.edges.map((imageEdge) => imageEdge.node.url);
const extractVideoUrls = (media: Media) =>
  media.edges
    .filter((mediaEdge) => mediaEdge.node.sources)
    .map((videoEdge) => videoEdge.node.sources.find((source) => source.format === 'mp4' && source.height > 720))
    .filter(notNil);

// GraphQL Queries // ? [REVISIT] move to graphql-loader
const PdpProductDetailsFragment = gql`
  fragment PdpProductDetails on Product {
    id
    title
    productType
    onlineStoreUrl
    tags
    vendor
    availableColors: metafield(namespace: "alo-swatch", key: "available-colors") {
      value
    }

    media(first: 250) {
      edges {
        node {
          ... on Video {
            id
            sources {
              width
              height
              url
              mimeType
              format
            }
            previewImage {
              id
              transformedSrc
            }
          }
        }
      }
    }
    attributes: metafields(
      identifiers: [
        { namespace: "attribs", key: "fit" }
        { namespace: "attribs", key: "fabrication" }
        { namespace: "attribs", key: "whyWeLoveIt" }
        { namespace: "attribs", key: "howToUse" }
        { namespace: "attribs", key: "tested & Approved" }
        { namespace: "attribs", key: "quickFit" }
        { namespace: "attribs", key: "quickFitSizeSelector" }
        { namespace: "attribs", key: "getTheLook" }
      ]
    ) {
      key
      value
    }
    # [REVISIT] Move all images into media? ^
    images(first: 25) {
      edges {
        node {
          # originalSrc
          url: transformedSrc(maxWidth: 750)
        }
      }
    }
    variants(first: 50) {
      edges {
        node {
          id
          sku
          selectedOptions {
            name
            value
          }
          image {
            # originalSrc
            url: transformedSrc(maxWidth: 750)
          }
          priceV2 {
            amount
          }
          compareAtPriceV2 {
            amount
          }
          requiresShipping
          availableForSale
          quantityAvailable
          estimatedShipStartDate: metafield(namespace: "preorder", key: "estimated_ship_date") {
            value
          }
        }
      }
    }
    availableForSale
    handle
    totalInventory
  }
`;
const GetPdpDataByColorwayProductsQuery = gql`
  query colorwayProducts($productSearchQuery: String!, $countryCode: CountryCode) @inContext(country: $countryCode) {
    products(first: 80, query: $productSearchQuery) {
      edges {
        node {
          ...PdpProductDetails
        }
      }
    }
  }
  ${PdpProductDetailsFragment}
`;
const GetPdpDataByProductIdsQuery = gql`
  query ProductDetailsByIds($ids: [ID!]!, $countryCode: CountryCode) @inContext(country: $countryCode) {
    nodes(ids: $ids) {
      id
      ...PdpProductDetails
    }
  }
  ${PdpProductDetailsFragment}
`;

const generateGetPdpDataQueryByHandles = (handles: string[]) => {
  const handlesQuery = handles
    .map(
      (handle, index) =>
        `
      _${index}: productByHandle(handle: "${handle}") {
        id
        ...PdpProductDetails
      }
    `
    )
    .join('');
  return gql`
    query GetPdpDetails{
      ${handlesQuery}
    }
    ${PdpProductDetailsFragment}
  `;
};

// * Utils

// add optional chaning to avoid crashing
const checkIfSetProduct = (data: WindowServerPdpData) =>
  !!data.metaFields.sets.colorway_product_handles || (data.metaFields.sets.advanced_set_config?.length || 0) > 1;

//@ts-expect-error // ? [REVISIT] fix assumption & type
const computeStyleId = (handle: string) => handle.split('-')[0].toLowerCase();

const GUID_REGEX = /_[0-9a-f]{8}-[0-9a-f]{4}-[0-5][0-9a-f]{3}-[089ab][0-9a-f]{3}-[0-9a-f]{12}/i;

const groupFullPriceSalePriceSwatches = SiteSettings?.groupFullPriceSalePriceSwatches ?? false;
